import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { ProcedureCodePicker } from './ProcedureCodePicker';
import type { DentalProcedureCodeRow } from '@/hooks/useProcedureCodes';
import type { DentalShortcutRow } from '@/hooks/useDentalShortcuts';

interface ShortcutEditModalProps {
  initial?: DentalShortcutRow | null;
  onSave: (values: {
    id?: string;
    label: string;
    procedureCode: string;
    color: string;
    displayOrder?: number;
    isActive: boolean;
  }) => Promise<void>;
  onClose: () => void;
  isSaving?: boolean;
}

export function ShortcutEditModal({ initial, onSave, onClose, isSaving }: ShortcutEditModalProps) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(initial?.label ?? '');
  const [code, setCode] = useState(initial?.procedure_code ?? '');
  const [color, setColor] = useState(initial?.color ?? '#3b82f6');
  const [displayOrder, setDisplayOrder] = useState(initial?.display_order ?? 99);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [showPicker, setShowPicker] = useState(false);

  const handleCodeSelect = (row: DentalProcedureCodeRow) => {
    setCode(row.code);
    if (!label) setLabel(row.name_da.slice(0, 20));
    setShowPicker(false);
  };

  const handleSave = async () => {
    if (!label.trim() || !code) return;
    await onSave({
      id: initial?.id,
      label: label.trim(),
      procedureCode: code,
      color,
      displayOrder,
      isActive,
    });
  };

  const isCreate = !initial;

  return (
    <>
      <Modal
        title={
          isCreate
            ? t('settings.dental.shortcuts.modal.createTitle', 'Nyt lynvalg')
            : t('settings.dental.shortcuts.modal.editTitle', 'Redigér lynvalg')
        }
        onClose={onClose}
      >
        <div className="space-y-4">
          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('settings.dental.shortcuts.modal.label', 'Label')}
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={24}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
              placeholder={t('settings.dental.shortcuts.modal.labelPlaceholder', 'Komposit 1F')}
            />
          </div>

          {/* Procedure code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('settings.dental.shortcuts.modal.procedureCode', 'Ydelseskode')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={code}
                readOnly
                className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                placeholder="—"
              />
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 dark:border-gray-700 dark:bg-gray-900 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
              >
                {t('settings.dental.shortcuts.modal.chooseCode', 'Vælg')}
              </button>
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('settings.dental.shortcuts.modal.color', 'Farve')}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-16 cursor-pointer rounded border border-gray-200 dark:border-gray-700"
              />
              <span className="text-sm font-mono text-gray-500 dark:text-gray-400">{color}</span>
              <div
                className="ml-auto h-8 w-24 rounded-lg text-center text-xs font-semibold text-white flex items-center justify-center"
                style={{ backgroundColor: color }}
              >
                {label || 'Preview'}
              </div>
            </div>
          </div>

          {/* Display order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('settings.dental.shortcuts.modal.displayOrder', 'Rækkefølge')}
            </label>
            <input
              type="number"
              value={displayOrder}
              min={1}
              max={999}
              onChange={(e) => setDisplayOrder(Number(e.target.value))}
              className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              onClick={() => setIsActive((v) => !v)}
              className={`relative h-5 w-9 rounded-full transition-colors ${isActive ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-4' : 'translate-x-0.5'}`}
              />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('settings.dental.shortcuts.modal.isActive', 'Aktiv')}
            </span>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t('dental.shortcuts.quickRegistration.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!label.trim() || !code || isSaving}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('settings.save')}
            </button>
          </div>
        </div>
      </Modal>

      {showPicker && (
        <ProcedureCodePicker
          asModal
          selectedCode={code}
          onSelect={handleCodeSelect}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}
